import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { placePopup, type Placement } from '../../lib/popup';
import { useMapStore } from '../../state/mapStore';
import { useViewMode } from '../../state/viewMode';
import { useOverlayPopup } from './overlayPopups';
import './overlayPopup.css';

/**
 * The popup of a feature a tool drew (overlayPopups.ts), in the identify popup's own look and place: on the map,
 * above the click (below it when the top would clip), closed by its ×, Escape, a click elsewhere, a theme switch or
 * a change of view. Rendered into the map's stage, where the identify popup lives.
 */
export function OverlayPopup() {
  const popup = useOverlayPopup((s) => s.popup);
  const close = useOverlayPopup((s) => s.close);
  const themeId = useMapStore((s) => s.themeId);
  const mode = useViewMode((s) => s.mode);
  const ref = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState<HTMLElement | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  useEffect(() => setStage(document.querySelector<HTMLElement>('.mm-stage')), []);

  // A new theme or view leaves the click behind.
  useEffect(() => close(), [themeId, mode, close]);

  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.offsetParent;
    if (!popup || !el || !(parent instanceof HTMLElement)) {
      setPlacement(null);
      return;
    }
    setPlacement(
      placePopup(
        popup.point,
        { width: el.offsetWidth, height: el.offsetHeight },
        { width: parent.clientWidth, height: parent.clientHeight },
      ),
    );
  }, [popup, stage]);

  useEffect(() => {
    if (!popup) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popup, close]);

  if (!popup || !stage) return null;
  const { content } = popup;
  return createPortal(
    <div
      ref={ref}
      className="popup mm-overlay-popup"
      style={
        placement
          ? { left: placement.left, top: placement.top }
          : { left: popup.point.x, top: popup.point.y, visibility: 'hidden' }
      }
      role="dialog"
      aria-label={content.heading}
    >
      <div className="popup-head">
        <h2>{content.heading}</h2>
        <button type="button" className="icon-btn" aria-label="Close" onClick={close}>
          ×
        </button>
      </div>
      {content.rows.length > 0 && (
        <dl>
          {content.rows.map((r, i) => (
            <div key={`${i}-${r.field}`} className="popup-row">
              <dt>{r.field}</dt>
              <dd>{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {content.lines.length > 0 && (
        <div className="mm-overlay-popup-text">
          {content.lines.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      )}
    </div>,
    stage,
  );
}
